<template>
  <div>
    <notifications></notifications>
    <navbar :docsName="docsName" />
    <div class="container-fluid row">
      <div class="col-xl-12">
        <div class="row">
          <div class="col-xl-12 block br-t-l-0 mb-3">
            <head-menu :col="12" class="d-xl-flex" :headline="navbarTitel"></head-menu>
            <div class="row mt-2">
              <div class="ml-4 mr-2">
                <button
                  v-if="editable"
                  v-shortkey.once="['ctrl', 's']"
                  class="btn btn-success"
                  :disabled="!valid || loading"
                  @click="speichern"
                  @shortkey="speichern"
                >
                  {{ $t("global.save") }}
                </button>
              </div>
              <div class="mr-2">
                <button class="btn btn-primary" @click="zurListe">
                  <font-awesome-icon icon="fa-solid fa-list" class="mr-2" />{{ $t("global.tolist") }}
                </button>
              </div>
              <div v-if="kontextRoute" class="mr-2">
                <router-link class="btn btn-primary" :to="kontextRoute">
                  <font-awesome-icon icon="fa-duotone fa-arrow-circle-right" class="mr-2" />{{ kontextBezeichnung }}
                </router-link>
              </div>
              <div class="ml-auto mr-3">
                <button v-if="editable" class="btn btn-danger" @click="oeffneLoeschenModal">
                  <font-awesome-icon icon="fa-duotone fa-trash" />
                  {{ $t("global.delete") }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-12">
        <container-headline :headline="$t('global.masterdata')" :col="6"></container-headline>
        <div class="row mb-3">
          <div class="col-xl-12 block br-t-l-0">
            <div class="eingabe-panel-kopf p-3">
              <div v-if="reservation" class="row">
                <div class="col-xl-3">
                  <div class="form-group">
                    <input :value="kontextBezeichnung" class="form-control" readonly placeholder=" " />
                    <label>{{ lehrgangId ? $t("global.educationcourse") : $t("global.shortevent") }}</label>
                  </div>
                </div>
                <div class="col-xl-3">
                  <div class="form-group">
                    <input :value="reservation.anreise" class="form-control" readonly placeholder=" " />
                    <label>{{ $t("global.arrival") }}</label>
                  </div>
                </div>
                <div class="col-xl-3">
                  <div class="form-group">
                    <input :value="reservation.abreise" class="form-control" readonly placeholder=" " />
                    <label>{{ $t("global.departure") }}</label>
                  </div>
                </div>
                <div class="col-xl-3">
                  <div class="form-group">
                    <input :value="reservation.naechte" class="form-control" readonly placeholder=" " />
                    <label>{{ $t("global.nights") }}</label>
                  </div>
                </div>
              </div>

              <!-- erst rendern, wenn der Kontext geladen ist (Module, Hotels, Typen) -->
              <zimmerreservation-formular
                v-if="kontextGeladen"
                ref="formular"
                formId="maske-zimmerreservation"
                :veranstaltung="veranstaltungKontextId"
                :lehrgang="lehrgangId"
                :module="module"
                :hotelkontingente="hotelkontingente"
                :reservationstypen="reservationstypen"
                :editable="editable"
                @valid="(val) => (valid = val)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <loeschen-modal id="zimmerreservation-maske-loeschen-modal" @confirmed="loeschen" />
    <loading-overlay v-show="loading"></loading-overlay>
  </div>
</template>

<script>
import AppApi from "@/api/AppApi";
import HeadMenu from "@/components/HeadMenu";
import ContainerHeadline from "@/components/ContainerHeadline";
import Navbar from "@/components/Navbar";
import LoadingOverlay from "@/components/global/LoadingOverlay";
import LoeschenModal from "@/components/Modals/LoeschenModal";
import ZimmerreservationFormular from "@/components/Zimmer/ZimmerreservationFormular";
import { apiErrorToAlert } from "@/utils/Errorhandler";

import store from "@/store";
import page from "@/mixins/Page";

/**
 * Maske Zimmerreservation. Der Kontext (Lehrgang/Modul bzw. Kurzveranstaltung) wird aus der Reservation
 * abgeleitet und über den Zimmer-Reiter-Endpunkt nachgeladen; danach wird das Formular initialisiert.
 * Neue Reservationen entstehen im Reiter bzw. auf der Maske Hotelkontingent (Kontext nötig).
 */
export default {
  name: "Zimmerreservation",
  components: { HeadMenu, Navbar, ContainerHeadline, LoadingOverlay, LoeschenModal, ZimmerreservationFormular },
  metaInfo() {
    return {
      titleTemplate: () => this.navbarTitel,
    };
  },
  mixins: [page],
  store,
  props: {
    id: { type: String, required: true },
  },
  data() {
    return {
      loading: false,
      valid: false,
      reservation: null,
      kontextGeladen: false,
      module: [],
      hotelkontingente: [],
      reservationstypen: [],
    };
  },
  computed: {
    lehrgangId() {
      return this.reservation?.lehrgang?.id || null;
    },
    veranstaltungKontextId() {
      return this.lehrgangId ? null : this.reservation?.veranstaltung?.id || null;
    },
    kontextBezeichnung() {
      if (!this.reservation) return "";
      if (this.lehrgangId) {
        const modul = this.reservation.veranstaltung?.bezeichnung;
        return modul ? `${this.reservation.lehrgang.bezeichnung} / ${modul}` : this.reservation.lehrgang.bezeichnung;
      }
      return this.reservation.veranstaltung?.bezeichnung || "";
    },
    kontextRoute() {
      if (this.lehrgangId) return { name: "bildungsgang", params: { lehrgangid: this.lehrgangId, anzeige: 6 } };
      if (this.veranstaltungKontextId)
        return { name: "Kurzveranstaltung", params: { kurzveranstaltungid: this.veranstaltungKontextId, anzeige: 9 } };
      return null;
    },
    navbarTitel() {
      const person = this.reservation?.person?.bezeichnung;
      return person ? `${this.$t("global.roomreservation")}: ${person}` : this.$t("global.roomreservation");
    },
  },
  watch: {
    id() {
      this.laden();
    },
  },
  created() {
    // editable kommt als data aus dem Page-Mixin (Computed würde dort ignoriert)
    this.editable = this.zimmerRecht("update");
    this.laden();
  },
  methods: {
    laden() {
      this.loading = true;
      this.kontextGeladen = false;
      AppApi.get("zimmerreservation/", { params: { id: this.id } })
        .then((response) => {
          this.reservation = response.data.reservation;
          const params = this.lehrgangId ? { lehrgang: this.lehrgangId } : { veranstaltung: this.veranstaltungKontextId };
          if (!this.lehrgangId && !this.veranstaltungKontextId) return null;
          return AppApi.get("zimmer-reiter/", { params });
        })
        .then((response) => {
          if (response) {
            this.module = response.data.module || [];
            this.hotelkontingente = response.data.hotelkontingente || [];
            this.reservationstypen = response.data.reservationstypen || [];
          }
          this.kontextGeladen = true;
          this.$nextTick(() => this.$refs.formular?.initialisieren(this.id));
        })
        .catch((e) => this.$notify(apiErrorToAlert(e)))
        .finally(() => {
          this.loading = false;
        });
    },
    speichern() {
      if (this.loading || !this.valid) return;
      this.loading = true;
      this.$refs.formular
        .speichern()
        .then((data) => {
          this.reservation = data.reservation;
        })
        .catch((e) => this.$notify(apiErrorToAlert(e)))
        .finally(() => {
          this.loading = false;
        });
    },
    zurListe() {
      this.$router.push({ name: "zimmerreservation-liste" });
    },
    oeffneLoeschenModal() {
      this.$bus.$emit("openLoeschenModal", "zimmerreservation-maske-loeschen-modal");
    },
    loeschen() {
      AppApi.delete("zimmerreservation/", { params: { ids: this.id } })
        .then(() => this.zurListe())
        .catch((e) => this.$notify(apiErrorToAlert(e)));
    },
  },
};
</script>
